import { memo, useEffect, useRef, useState } from "react";
import { thumbnailBlob } from "../../api/images";
import type { ImageOut } from "../../api/types";
import { isAugmented } from "../../lib/augmentImages";
import { cardLabel } from "../../lib/dashboard";
import { ThumbCache } from "../../lib/thumbCache";

/** Shared by every card: authenticated thumbnails as object URLs (see lib/thumbCache.ts). */
export const thumbs = new ThumbCache((id, signal) => thumbnailBlob(id, signal));

/** One thumbnail cell. Fetches its picture only once it scrolls near the viewport. */
const Card = memo(function Card({ image, selected, onClick, onToggle, onOpen }: {
  image: ImageOut; selected: boolean; onClick: (e: React.MouseEvent) => void; onToggle: () => void; onOpen: () => void;
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const el = holder.current;
    if (!el) return;
    let alive = true;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        thumbs.get(image.id).then((u) => { if (alive) setUrl(u); }).catch(() => { /* a broken thumbnail leaves the placeholder */ });
      }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
  }, [image.id]);
  const { name, detail, status } = cardLabel(image);
  return (
    <div className="card" role="option" aria-selected={selected} data-testid={`card-${image.filename}`}
      onClick={onClick} onDoubleClick={onOpen} ref={holder} title={image.filename}>
      <div className="thumb">
        {url ? <img src={url} alt="" draggable={false} /> : null}
        <span className="card-check" role="checkbox" aria-checked={selected} aria-label={`Select ${image.filename}`} data-testid="select-box"
          onClick={(e) => { e.stopPropagation(); onToggle(); }} onDoubleClick={(e) => e.stopPropagation()} />
        {isAugmented(image) && <span className="aug-badge" data-testid="aug-badge"
          title={`Augmented copy${image.provenance?.from_filename ? ` of ${String(image.provenance.from_filename)}` : ""}${Array.isArray(image.provenance?.changes) ? `: ${(image.provenance!.changes as string[]).join(", ")}` : ""}`}>Augmented</span>}
      </div>
      <div className="card-caption">
        <span className="name">{name}</span>
        <span className={`pill ${status}`} data-testid="status-pill"><i />{detail}</span>
      </div>
    </div>
  );
});

export function Gallery({ images, selected, onSelect, onToggle, onOpen }: {
  images: ImageOut[]; selected: ReadonlySet<number>;
  onSelect: (id: number, e: React.MouseEvent) => void; onToggle: (id: number) => void; onOpen: (id: number) => void;
}) {
  return (
    <div className="gallery" role="listbox" aria-multiselectable="true" data-testid="gallery">
      {images.map((img) => (
        <Card key={img.id} image={img} selected={selected.has(img.id)} onClick={(e) => onSelect(img.id, e)} onToggle={() => onToggle(img.id)} onOpen={() => onOpen(img.id)} />
      ))}
    </div>
  );
}
